import { InputAmount, InputSelect } from '@vrplatform/components';
import { useTeam } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { bankersRoundToCents, formatCurrency } from '@vrplatform/utils';
import {
  Group,
  Input as MantineInput,
  InputBase,
  InputWrapper as MantineInputWrapper,
  Popover,
  Stack,
  Text,
} from '@mantine/core';
import { Controller, useWatch } from 'react-hook-form';
import { useTaxRatesQuery } from '@vrplatform/data';
import { useExpenseForm } from '../useExpenseForm';
import { getExpenseLineCentTotal } from '../../_hooks';
import { useDisclosure } from '@mantine/hooks';

type AmountFormId = `lines.${number}.centTotal` | `lines.${number}.markup`;
type TaxFormId = `lines.${number}.taxId` | `lines.${number}.markupTaxId`;

type Props = {
  amountFormId: AmountFormId;
  taxFormId: TaxFormId;
  rowIndex: number;
  onAddTaxRate?: () => void;
};

export const AmountTaxPopover = ({
  amountFormId,
  taxFormId,
  rowIndex,
  onAddTaxRate,
}: Props) => {
  const [{ currency }] = useTeam();
  const methods = useExpenseForm();
  const { data: rates, error, isLoading: loadingRates } = useTaxRatesQuery();

  const [opened, handlers] = useDisclosure(false);

  const placeholder = formatCurrency(0, currency);
  const isMarkup = taxFormId.includes('markupTaxId');

  const party = useWatch({
    control: methods.control,
    name: `lines.${rowIndex}.party` as const,
  });

  const locked = useWatch({
    control: methods.control,
    name: `lines.${rowIndex}.locked` as const,
  });

  const taxId = useWatch({
    control: methods.control,
    name: taxFormId,
  });

  const label = isMarkup ? 'Markup' : 'Amount';
  const taxLabel = isMarkup ? 'Markup tax' : 'Amount tax';

  const amountDefaultValue = methods.getValues(amountFormId) ?? 0;
  const taxDefaultValue = methods.getValues(taxFormId) ?? null;

  return (
    <Controller
      control={methods.control}
      name={amountFormId}
      defaultValue={amountDefaultValue}
      render={({ field, fieldState }) => {
        const isDisabled =
          field.disabled ||
          methods.context.lock?.expense === 'locked' ||
          !!locked ||
          (isMarkup && party !== 'owners');

        const customBottomActions = onAddTaxRate
          ? [
              {
                id: 'add-tax-rate',
                label: 'Other Rate - Add tax rate',
                description: 'If you are missing a tax rate, add it now.',
                onSubmit: onAddTaxRate,
                icon: <Icon icon="PlusIcon" size={16} />,
              },
            ]
          : undefined;

        return (
          <Popover
            position="bottom-end"
            shadow="md"
            radius="md"
            withArrow
            withinPortal
            width={'auto'}
            disabled={isDisabled}
            opened={opened}
            onChange={handlers.toggle}
          >
            <Popover.Target>
              <InputBase
                component="button"
                type="button"
                pointer
                data-testid={`${field.name}-trigger`}
                error={fieldState.error?.message}
                disabled={isDisabled}
                radius="md"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handlers.toggle();
                }}
                sx={(theme) => {
                  const activeStyles = {
                    borderColor: theme.colors[theme.primaryColor][6],
                    backgroundColor: theme.colors[theme.primaryColor][0] + 70,
                  };

                  const errorStyles = {
                    boxShadow: `0px 0px 0px 2px ${theme.colors.red[4]}40`,
                    borderColor: theme.colors.red[6],
                    backgroundColor: theme.colors.red[1],
                  };

                  return {
                    width: '100%',
                    button: {
                      textAlign: 'right',
                      width: '100%',
                    },
                    '.mantine-Input-input': {
                      ...(opened ? activeStyles : {}),
                      ...(fieldState.error ? errorStyles : {}),
                      '&:disabled': { color: 'unset' },
                    },
                  };
                }}
              >
                {typeof field.value === 'number' ? (
                  <MantineInput.Label fw={400}>
                    {formatCurrency(
                      getExpenseLineCentTotal({
                        centTotal: field.value,
                        taxId,
                        markup: 0,
                        markupTaxId: null,
                        party,
                        rates: rates ?? [],
                      }).centTotal / 100,
                      currency
                    )}
                  </MantineInput.Label>
                ) : (
                  <MantineInput.Placeholder>
                    {placeholder}
                  </MantineInput.Placeholder>
                )}
              </InputBase>
            </Popover.Target>
            <Popover.Dropdown
              onClick={(event) => event.stopPropagation()}
              p="md"
            >
              <Stack gap="sm">
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Text size="sm" fw={500}>
                    {label}
                  </Text>
                  <InputAmount
                    value={
                      typeof field.value === 'number'
                        ? field.value / 100
                        : (field.value ?? '')
                    }
                    data-testid={field.name}
                    name={field.name}
                    setValue={(value) => {
                      if (typeof value === 'string') {
                        if (value === '') {
                          field.onChange('');
                          return;
                        }

                        const bankers = bankersRoundToCents(value);
                        if (bankers === field.value) return;
                      }
                      field.onChange(bankersRoundToCents(value));
                    }}
                    w={130}
                    decimalScale={2}
                    disabled={isDisabled}
                    error={fieldState.error?.message}
                    placeholder={placeholder}
                    hideControls
                    styles={{
                      input: {
                        textAlign: 'right',
                      },
                    }}
                  />
                </Group>
                <Group
                  justify="space-between"
                  align="center"
                  wrap="nowrap"
                  gap="xl"
                >
                  <Text size="sm" fw={500}>
                    {taxLabel}
                  </Text>
                  <Controller
                    control={methods.control}
                    name={taxFormId}
                    defaultValue={taxDefaultValue}
                    render={({ field: taxField, fieldState: taxState }) => {
                      const value =
                        rates?.find((x) => x.value === taxField.value) || null;

                      return (
                        <MantineInputWrapper error={taxState.error?.message}>
                          <InputSelect
                            type="single"
                            value={value}
                            setValue={(value) => {
                              taxField.onChange(value?.value);
                            }}
                            dropdownProps={{
                              position: 'bottom-end',
                              withinPortal: false,
                            }}
                            inputProps={{
                              loadingQuery: loadingRates,
                              error: !!taxState.error,
                              placeholder: 'Select tax',
                              withClearButton: true,
                              disabled: isDisabled,
                              width: 170,
                            }}
                            customBottomActions={customBottomActions}
                            data={{
                              options: rates || [],
                              error,
                              loading: loadingRates,
                            }}
                          />
                        </MantineInputWrapper>
                      );
                    }}
                  />
                </Group>
              </Stack>
            </Popover.Dropdown>
          </Popover>
        );
      }}
    />
  );
};
